"use client";

import { Check, ChevronDown, Search } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../patterns/popover/popover";
import { Button } from "../button/button";
import { Input } from "../input/input";
import { Text } from "../text/text";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  id?: string;
  options: SearchableSelectOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
}

export function SearchableSelect({
  id,
  options,
  value,
  onValueChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
}: Readonly<SearchableSelectProps>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery)
    );
  }, [options, query]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) {
      return;
    }
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  const handleSelect = (option: SearchableSelectOption) => {
    onValueChange(option.value);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <div className="[&>button]:w-full [&>button]:justify-between">
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            disabled={disabled}
            id={id}
            role="combobox"
            type="button"
            variant="outline"
          >
            <span className={selectedOption ? undefined : "text-zinc-500"}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronDown aria-hidden className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <div className="relative border-b p-2">
          <Search
            aria-hidden
            className="-translate-y-1/2 absolute top-1/2 left-5 size-4 text-zinc-500"
          />
          <Input
            autoFocus
            className="pl-9"
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            value={query}
          />
        </div>
        <ul className="max-h-72 overflow-y-auto p-1">
          {filteredOptions.length === 0 && (
            <li className="px-3 py-2">
              <Text color="muted" variant="body-sm">
                {emptyMessage}
              </Text>
            </li>
          )}
          {filteredOptions.map((option) => (
            <li
              className="[&>button]:w-full [&>button]:justify-between [&>button]:rounded-sm [&>button]:text-left"
              key={option.value}
            >
              <Button
                onClick={() => handleSelect(option)}
                type="button"
                variant="ghost"
              >
                {option.label}
                {option.value === value && (
                  <Check aria-hidden className="size-4 shrink-0" />
                )}
              </Button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
